import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import WarehouseForm from '@/components/warehouse/WarehouseForm';
import WarehouseService from '@/api/warehouses.service';

interface WarehouseFormData {
  name: string;
  code: string;
  description: string;
  address: string;
  contactPerson: string;
  contactPhone: string;
  contactEmail: string;
  capacity: number | '';
  timezone: string;
  status: 'active' | 'inactive';
}

interface Warehouse {
  publicId: string;
  name: string;
  code: string;
  description?: string;
  address?: string;
  contactPerson?: string;
  contactPhone?: string;
  contactEmail?: string;
  capacity?: number;
  timezone?: string;
  status: 'active' | 'inactive';
  createdAt: string;
  updatedAt: string;
}

const EditWarehouse: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  
  const [warehouse, setWarehouse] = useState<Warehouse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    if (id) {
      fetchWarehouse();
    }
  }, [id]);

  const fetchWarehouse = async () => {
    if (!id) return;
    
    try {
      setIsLoadingData(true);
      const response = await WarehouseService.getWarehouse(id);
      
      if (response.data?.success) {
        setWarehouse(response.data.warehouse);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response.data?.message || "Failed to fetch warehouse details",
        });
        navigate('/warehouses');
      }
    } catch (error) {
      console.error('Error fetching warehouse:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch warehouse details",
      });
      navigate('/warehouses');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleSubmit = async (formData: WarehouseFormData) => {
    if (!id) return;
    
    setIsLoading(true);
    
    try {
      // Prepare data for API
      const warehouseData = {
        name: formData.name,
        code: formData.code,
        description: formData.description || undefined,
        address: formData.address || undefined,
        contactPerson: formData.contactPerson || undefined,
        contactPhone: formData.contactPhone || undefined,
        contactEmail: formData.contactEmail || undefined,
        capacity: formData.capacity === '' ? undefined : Number(formData.capacity),
        timezone: formData.timezone,
        status: formData.status
      };

      const response = await WarehouseService.updateWarehouse(id, warehouseData);

      if (response.data?.success) {
        toast({
          title: "Success",
          description: "Warehouse updated successfully",
        });
        navigate('/warehouses');
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response.data?.message || "Failed to update warehouse",
        });
      }
    } catch (error: any) {
      console.error('Error updating warehouse:', error);
      
      let errorMessage = "Failed to update warehouse";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      toast({
        variant: "destructive",
        title: "Error",
        description: errorMessage,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/warehouses');
  };

  if (isLoadingData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!warehouse) {
    return (
      <div className="text-center py-8">
        <h3 className="text-lg font-medium text-gray-900">Warehouse not found</h3>
        <p className="text-gray-500">The warehouse you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/warehouses')} className="mt-4">
          Back to Warehouses
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/warehouses')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Warehouses
        </Button>
      </div>

      <div>
        <h1 className="text-3xl font-bold tracking-tight">Edit Warehouse</h1>
        <p className="text-muted-foreground">
          Update warehouse information and settings
        </p>
      </div>

      {/* Form */}
      <WarehouseForm
        mode="edit"
        initialData={{
          name: warehouse.name,
          code: warehouse.code,
          description: warehouse.description || '',
          address: warehouse.address || '',
          contactPerson: warehouse.contactPerson || '',
          contactPhone: warehouse.contactPhone || '',
          contactEmail: warehouse.contactEmail || '',
          capacity: warehouse.capacity || '',
          timezone: warehouse.timezone || 'UTC',
          status: warehouse.status
        }}
        onSubmit={handleSubmit}
        onCancel={handleCancel}
        isLoading={isLoading}
      />
    </div>
  );
};

export default EditWarehouse; 